import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Users, UserPlus, MapPin, CheckCircle, Loader2, RefreshCw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { toast } from 'sonner';

export default function PeopleYouMayKnow({ variant = 'sidebar', limit = 6 }) {
  const { isAuthenticated, getAuthHeaders } = useAuth();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [followingIds, setFollowingIds] = useState(new Set());

  const fetchSuggestions = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/suggestions/people-you-may-know?limit=${limit}`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setSuggestions(data.suggestions || []);
      }
    } catch (err) {
      console.error('Failed to fetch suggestions:', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, limit, getAuthHeaders]);

  useEffect(() => {
    fetchSuggestions();
  }, [fetchSuggestions]);

  const handleFollow = async (userId) => {
    try {
      const res = await fetch(`${API}/users/${userId}/follow`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        setFollowingIds(prev => new Set([...prev, userId]));
        setSuggestions(prev => prev.filter(s => s.user_id !== userId));
        toast.success('Following!');
      }
    } catch {
      toast.error('Failed to follow');
    }
  };

  if (!isAuthenticated || (suggestions.length === 0 && !loading)) return null;

  if (variant === 'sidebar') {
    return (
      <Card className="glass-card border-border p-4" data-testid="pymk-sidebar">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-bold text-sm uppercase flex items-center gap-2">
            <Users className="w-4 h-4 text-orange-400" />
            People You May Know
          </h3>
          <button onClick={fetchSuggestions} className="text-muted-foreground hover:text-foreground transition-colors">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
        {loading ? (
          <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="space-y-3">
            {suggestions.slice(0, 5).map(person => (
              <div key={person.user_id} className="flex items-center gap-3" data-testid={`pymk-card-${person.handle}`}>
                <Link to={person.handle ? `/u/${person.handle}` : '#'} className="flex-shrink-0">
                  <div className="w-9 h-9 rounded-full overflow-hidden bg-muted/50">
                    {person.avatar_url ? (
                      <img src={person.avatar_url} alt={person.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-xs font-bold">
                        {(person.name || '?')[0]}
                      </div>
                    )}
                  </div>
                </Link>
                <div className="flex-1 min-w-0">
                  <Link to={person.handle ? `/u/${person.handle}` : '#'} className="block">
                    <p className="text-sm font-semibold truncate hover:text-orange-400 transition-colors">
                      {person.name}
                      {person.is_verified && <CheckCircle className="w-3 h-3 text-blue-400 inline ml-1" />}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {person.role && <span className="capitalize">{person.role}</span>}
                      {person.city && <span> · {person.city}</span>}
                    </p>
                  </Link>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-xs bg-orange-500/20 hover:bg-orange-500/40 text-orange-400"
                  onClick={() => handleFollow(person.user_id)}
                  data-testid={`pymk-follow-${person.handle}`}
                >
                  <UserPlus className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>
    );
  }

  // Full/card variant for Discover page
  return (
    <div data-testid="pymk-full">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading font-bold text-lg uppercase flex items-center gap-2">
          <Users className="w-5 h-5 text-orange-400" />
          People You May Know
        </h2>
        <button onClick={fetchSuggestions} className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {suggestions.map(person => (
            <Card key={person.user_id} className="glass-card border-border p-4 text-center hover-lift" data-testid={`pymk-card-${person.handle}`}>
              <Link to={person.handle ? `/u/${person.handle}` : '#'}>
                <div className="w-16 h-16 rounded-full overflow-hidden bg-muted/50 mx-auto mb-3">
                  {person.avatar_url ? (
                    <img src={person.avatar_url} alt={person.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-lg font-bold bg-gradient-to-br from-orange-500/30 to-pink-500/30">
                      {(person.name || '?')[0]}
                    </div>
                  )}
                </div>
                <p className="font-semibold text-sm truncate">
                  {person.name}
                  {person.is_verified && <CheckCircle className="w-3 h-3 text-blue-400 inline ml-1" />}
                </p>
                <p className="text-xs text-muted-foreground truncate capitalize">{person.role}</p>
                {person.city && (
                  <p className="text-xs text-muted-foreground truncate flex items-center justify-center gap-1 mt-0.5">
                    <MapPin className="w-3 h-3" />{person.city}
                  </p>
                )}
                {person.mutual_follow && (
                  <Badge className="mt-1 text-[10px] bg-blue-500/20 text-blue-400">Mutual</Badge>
                )}
              </Link>
              <Button
                size="sm"
                className="mt-3 w-full h-7 text-xs bg-orange-500 hover:bg-orange-600"
                onClick={(e) => { e.preventDefault(); handleFollow(person.user_id); }}
                data-testid={`pymk-follow-${person.handle}`}
              >
                <UserPlus className="w-3 h-3 mr-1" /> Follow
              </Button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
